// Smart Daara - PDF Viewer Component (scroll-based progression via react-pdf)
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useLanguage } from '../../contexts/LanguageContext';
import { progressAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut,
  FileText, CheckCircle, BookmarkCheck, Maximize, Minimize, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function PDFViewer({
  lessonId,
  pdfUrl,
  totalPages = 1,
  initialPage = 1,
  onComplete
}) {
  const { language } = useLanguage();
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [maxPageReached, setMaxPageReached] = useState(initialPage);
  const [zoom, setZoom] = useState(100);
  const [isCompleted, setIsCompleted] = useState(false);
  const [lastSavedPage, setLastSavedPage] = useState(initialPage);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [useFallback, setUseFallback] = useState(false);
  const [pageWidth, setPageWidth] = useState(800);
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const pageRefs = useRef({});
  const pendingScrollRef = useRef(null);
  const armedRef = useRef(false);
  const saveTimeoutRef = useRef(null);
  const completedRef = useRef(false);
  const lastSaveCallRef = useRef(0);

  // Same-origin proxied file (Bunny CDN doesn't send CORS headers for pdf.js fetch)
  const pdfFile = useMemo(() => {
    if (!pdfUrl) return null;
    return {
      url: `${process.env.REACT_APP_BACKEND_URL}/api/files/pdf?url=${encodeURIComponent(pdfUrl)}`,
      httpHeaders: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    };
  }, [pdfUrl]);

  // Track fullscreen state (Esc handled natively by the browser)
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Load saved progress on mount
  useEffect(() => {
    loadSavedProgress();
  }, [lessonId]);

  const loadSavedProgress = async () => {
    if (!lessonId) return;
    try {
      const response = await progressAPI.getLessonProgress(lessonId);
      if (response.data) {
        const savedPage = response.data.pdf_current_page || 1;
        const savedMax = Math.max(response.data.pdf_max_page_reached || 1, savedPage);
        const completed = response.data.is_completed || false;

        setCurrentPage(savedPage);
        setMaxPageReached(savedMax);
        setLastSavedPage(savedPage);
        setIsCompleted(completed);
        completedRef.current = completed;
        pendingScrollRef.current = savedPage;

        if (savedPage > 1 && !completed) {
          toast.info(
            language === 'fr'
              ? `Reprise à la page ${savedPage}`
              : `Resuming from page ${savedPage}`
          );
        }
      }
    } catch (error) {
      console.error('Error loading saved progress:', error);
    }
  };

  // Save progress when page changes (debounced)
  useEffect(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveProgress(currentPage);
    }, 800);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [currentPage]);

  // Periodic save every 5 s while reading (safety net if debounce was missed)
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (currentPage !== lastSavedPage) {
        saveProgress(currentPage);
      }
    }, 5000);
    return () => clearInterval(intervalId);
  }, [currentPage, lastSavedPage]);

  // Robust auto-completion: whenever all pages have been reached, complete the lesson
  useEffect(() => {
    if (!lessonId || completedRef.current) return;
    if (maxPageReached >= totalPages) {
      completedRef.current = true;
      setIsCompleted(true);
      progressAPI.updateLessonProgress(lessonId, { pdf_current_page: totalPages })
        .catch((e) => console.error('Error completing lesson:', e));
      if (onComplete) onComplete();
    }
  }, [maxPageReached, totalPages, lessonId]);

  const saveProgress = async (page) => {
    if (!lessonId) return;
    // Throttle: skip calls closer than 300 ms
    const now = Date.now();
    if (now - lastSaveCallRef.current < 300) return;
    lastSaveCallRef.current = now;
    try {
      await progressAPI.updateLessonProgress(lessonId, {
        pdf_current_page: page
      });
      setLastSavedPage(page);

      // Completion: all pages consulted
      const effectiveMax = Math.max(maxPageReached, page);
      if (effectiveMax >= totalPages && !completedRef.current) {
        completedRef.current = true;
        setIsCompleted(true);
        if (onComplete) onComplete();
      }
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  // Measure available width for page rendering
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || useFallback) return;
    const update = () => setPageWidth(Math.max(300, el.clientWidth - 48));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [useFallback, numPages, isFullscreen]);

  // Scroll tracking on the INNER frame: the page closest to the frame middle
  // becomes the current page (layout changes alone never fire 'scroll')
  useEffect(() => {
    if (!numPages || useFallback) return;
    const container = scrollRef.current;
    if (!container) return;
    armedRef.current = false;

    const scrollToPage = (p) => {
      const el = pageRefs.current[p];
      if (!el) return;
      const cRect = container.getBoundingClientRect();
      const eRect = el.getBoundingClientRect();
      container.scrollTop = container.scrollTop + (eRect.top - cRect.top) - 8;
    };

    const positionTimer = setTimeout(() => {
      scrollToPage(pendingScrollRef.current || 1);
      pendingScrollRef.current = null;
    }, 300);
    const armTimer = setTimeout(() => { armedRef.current = true; }, 1200);

    let lastRun = 0;
    const onScroll = () => {
      if (!armedRef.current) return;
      const now = Date.now();
      if (now - lastRun < 200) return;
      lastRun = now;
      const cRect = container.getBoundingClientRect();
      const mid = cRect.top + cRect.height / 2;
      let best = null;
      let bestDist = Infinity;
      Object.entries(pageRefs.current).forEach(([p, el]) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const d = Math.abs((r.top + r.height / 2) - mid);
        if (d < bestDist) { bestDist = d; best = parseInt(p, 10); }
      });
      if (best) {
        setCurrentPage(best);
        setMaxPageReached(prev => Math.max(prev, best));
      }
    };
    container.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      clearTimeout(positionTimer);
      clearTimeout(armTimer);
      container.removeEventListener('scroll', onScroll);
    };
  }, [numPages, useFallback]);

  const effectiveTotal = numPages || totalPages;

  const goToPage = (page) => {
    if (page >= 1 && page <= effectiveTotal) {
      setCurrentPage(page);
      setMaxPageReached(prev => Math.max(prev, page));
      if (!useFallback) {
        const container = scrollRef.current;
        const el = pageRefs.current[page];
        if (container && el) {
          const cRect = container.getBoundingClientRect();
          const eRect = el.getBoundingClientRect();
          container.scrollTo({ top: container.scrollTop + (eRect.top - cRect.top) - 8, behavior: 'smooth' });
        }
      }
    }
  };

  const handleZoom = (delta) => {
    setZoom(prev => Math.max(50, Math.min(200, prev + delta)));
  };

  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else container.requestFullscreen();
  };

  const markAsRead = () => {
    setMaxPageReached(totalPages);
    lastSaveCallRef.current = 0; // bypass throttle for explicit user action
    saveProgress(totalPages);
  };

  const progressPercent = Math.min(100, (maxPageReached / totalPages) * 100);

  return (
    <div className="flex flex-col h-full bg-slate-100 rounded-lg overflow-hidden" ref={containerRef} data-testid="pdf-viewer">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              data-testid="pdf-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium min-w-[80px] text-center" data-testid="pdf-page-indicator">
              {currentPage} / {effectiveTotal}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= effectiveTotal}
              data-testid="pdf-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="h-6 w-px bg-slate-200" />

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => handleZoom(-10)}>
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium min-w-[50px] text-center">{zoom}%</span>
            <Button variant="ghost" size="sm" onClick={() => handleZoom(10)}>
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={toggleFullscreen} data-testid="pdf-fullscreen-btn">
              {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Completion Status */}
        <div className="flex items-center gap-3">
          {currentPage >= totalPages && !isCompleted && (
            <Button
              size="sm"
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              onClick={markAsRead}
              data-testid="pdf-mark-read-btn"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              {language === 'fr' ? 'Marquer comme lu' : 'Mark as read'}
            </Button>
          )}
          {isCompleted && (
            <div className="flex items-center gap-1 text-emerald-600 text-sm font-medium" data-testid="pdf-completed-badge">
              <CheckCircle className="w-4 h-4" />
              {language === 'fr' ? 'Terminé' : 'Completed'}
            </div>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-4 py-2 bg-white border-b border-slate-200">
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">
            {language === 'fr' ? 'Progression' : 'Progress'}
          </span>
          <Progress value={progressPercent} className="flex-1 h-2" />
          <span className="text-xs font-medium text-emerald-600" data-testid="pdf-progress-percent">
            {Math.round(progressPercent)}%
          </span>
        </div>
        <p className="text-[11px] text-slate-400 mt-1">
          {language === 'fr'
            ? `${maxPageReached} page(s) consultée(s) sur ${totalPages}`
            : `${maxPageReached} page(s) viewed out of ${totalPages}`}
        </p>
      </div>

      {/* PDF Content — fixed-height frame, scrolling happens INSIDE */}
      <div
        className={`overflow-auto p-4 ${isFullscreen ? 'flex-1' : ''}`}
        style={isFullscreen ? undefined : { height: '600px' }}
        ref={scrollRef}
      >
        {pdfUrl ? (
          useFallback ? (
            <div
              className="bg-white rounded-lg shadow-sm mx-auto transition-transform"
              style={{ width: `${zoom}%`, maxWidth: '100%', minHeight: '600px' }}
            >
              <div className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-200 rounded-t-lg" data-testid="pdf-fallback-notice">
                {language === 'fr'
                  ? 'Mode de secours : utilisez les flèches ◀ ▶ pour avancer (le défilement n\'est pas suivi)'
                  : 'Fallback mode: use the ◀ ▶ arrows to advance (scrolling is not tracked)'}
              </div>
              <iframe
                key={currentPage}
                src={`${pdfUrl}#page=${currentPage}&toolbar=0&navpanes=0`}
                className="w-full h-[600px] rounded-lg"
                title="PDF Document"
              />
            </div>
          ) : (
            <Document
              file={pdfFile}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={(err) => {
                console.error('react-pdf load error, falling back to iframe:', err);
                setUseFallback(true);
              }}
              loading={
                <div className="flex items-center justify-center h-64 text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  {language === 'fr' ? 'Chargement du document...' : 'Loading document...'}
                </div>
              }
            >
              <div className="flex flex-col items-center gap-4">
                {Array.from({ length: numPages || 0 }, (_, i) => (
                  <div
                    key={i + 1}
                    data-page={i + 1}
                    ref={(el) => { pageRefs.current[i + 1] = el; }}
                    className="bg-white rounded-lg shadow-sm"
                    data-testid={`pdf-page-${i + 1}`}
                  >
                    <Page
                      pageNumber={i + 1}
                      width={Math.round(pageWidth * zoom / 100)}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      loading={<div style={{ width: Math.round(pageWidth * zoom / 100), height: 400 }} />}
                    />
                  </div>
                ))}
              </div>
            </Document>
          )
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-slate-500">
            <FileText className="w-16 h-16 mb-4 text-slate-300" />
            <p>{language === 'fr' ? 'Document PDF non disponible' : 'PDF document not available'}</p>
          </div>
        )}
      </div>

      {/* Footer with completion hint */}
      <div className="px-4 py-3 bg-white border-t border-slate-200">
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">
            {language === 'fr'
              ? `Pour terminer la leçon : consultez toutes les pages (${totalPages} au total) — faites défiler ou utilisez les flèches`
              : `To complete the lesson: view all pages (${totalPages} total) — scroll or use the arrows`}
          </p>
          {lastSavedPage > 1 && (
            <div className="flex items-center gap-1 text-xs text-emerald-600">
              <BookmarkCheck className="w-3 h-3" />
              {language === 'fr' ? `Sauvegardé à la page ${lastSavedPage}` : `Saved at page ${lastSavedPage}`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
